"use client";

/**
 * ToolThinkingDisplay - Compact inline status line for tool calls
 *
 * Replaces verbose tool cards when debug mode is OFF.
 * Shows: spinner (executing) / checkmark (complete) / error icon + summary text.
 *
 * While executing it shows an input-derived phrase ("Querying data: …"); on
 * completion it replaces that with the essential result value ("342 candidates"),
 * so normal mode surfaces the actual answer, not just the action. Rich per-tool
 * displays remain available in debug/verbose mode.
 */

import { Spinner } from "@/ui";
import { colors, spacing, typography } from "@/ui/tokens";
import { Streamdown } from "streamdown";
import remarkGfm from "remark-gfm";
import { getToolThinkingSummary } from "@/lib/sola/utils/toolThinkingSummary";
import { getFailedResultText, getToolResultLine, isFailedToolResult } from "@/lib/sola/utils/toolResultSummary";
import type { AggregatedToolCall } from "@/lib/sola/types/tools";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import styles from "./ToolThinkingDisplay.module.css";

interface ToolThinkingDisplayProps {
  toolCall: AggregatedToolCall;
}

export function ToolThinkingDisplay({ toolCall }: ToolThinkingDisplayProps) {
  const { toolName, state, input } = toolCall;

  // A tool that returns nothing is still complete once its output is available.
  const failedResult = state === "output-available" && isFailedToolResult(toolCall.finalOutput);
  const isError = state === "output-error" || state === "output-denied" || failedResult;
  const isComplete = state === "output-available" && !failedResult;
  const isExecuting = !isComplete && !isError;

  // On completion, prefer the essential result value; fall back to the
  // input-derived phrase when the tool has no mapped result line. On failure,
  // show why it failed: thrown errors arrive as errorText, returned ones in the output.
  const resultLine = isComplete ? getToolResultLine(toolCall) : null;
  const errorLine = isError
    ? (state === "output-denied" ? "Not run: permission was denied" : toolCall.errorText || getFailedResultText(toolCall.finalOutput))
    : null;
  const text =
    resultLine ??
    errorLine ??
    getToolThinkingSummary(toolName, input);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[2],
        paddingTop: 2,
        paddingBottom: 2,
        minHeight: 24,
      }}
    >
      {/* Status icon */}
      {isExecuting && <Spinner size="xs" color="current" />}
      {isComplete && (
        <CheckCircleOutlineIcon
          sx={{ fontSize: 16, color: colors.signal.success }}
        />
      )}
      {isError && (
        <ErrorOutlineIcon
          sx={{ fontSize: 16, color: colors.signal.danger }}
        />
      )}

      {/* Summary text (inline markdown for bold/links) */}
      <div
        className={styles.summary}
        style={{
          fontSize: typography.scale.sm[0],
          color: isError ? colors.signal.danger : colors.text.muted,
          lineHeight: 1.4,
          minWidth: 0,
        }}
      >
        <Streamdown remarkPlugins={[remarkGfm]}>{text}</Streamdown>
      </div>
    </div>
  );
}
